import { Tabs, Flex, Divider, Row, Tag, Typography } from 'antd';
import classes from '../../common/styles/FormStyles.module.scss';
import SignUpForm from '../../components/auth/SignUpForm';
import SignInForm from '../../components/auth/SignInForm';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import MarketingConsentCheckbox from '../../components/auth/MarketingConsentCheckbox';
import FacebookSignInButton from '../../components/auth/FacebookSignInButton';
import { useState, useEffect } from 'react';
import { notification } from 'antd';
import { colors, fontSize, primitiveColors, radius, spacing } from '../../theming';
import logo from '../../assets/check-vin-logo.png';
import { useTranslation } from 'react-i18next';
import { useMedia } from './../../hooks/useMediaQuery';
import { useSearchParams } from 'react-router-dom';
import { useVinCodeService } from '../../services/vinCodeService';
import AppLoader from '../../common/components/AppLoader';
import { Link } from 'react-router-dom';
import { routesPath } from '../../common/constants/routesPath';
import { constants } from '../../common/constants/constants';
import { getLandingVinQuery } from '../../utils/landingHandoff';

function Auth() {
    const { decodeVin } = useVinCodeService();

    const [api, contextHolder] = notification.useNotification();
    const [isLoading, setIsLoading] = useState(false);
    const [vinData, setVinData] = useState(null);

    const openNotification = ({ type = 'error', message = '', description = '', placement = 'top' }) => {
        api[type]({
            message: message,
            description: description,
            placement: placement,
        });
    };

    const { t } = useTranslation(['auth', 'form', 'apiResponses', 'general']);
    const { isSmallDevice } = useMedia();

    const [activeTabKey, setActiveTabKey] = useState('0');
    // one choice for every sign-up method (email and Google); only the Sign-up tab shows it or sends it
    const [marketingConsent, setMarketingConsent] = useState(false);
    const toggleTab = () => setActiveTabKey(activeTabKey === '0' ? '1' : '0');
    const [params] = useSearchParams();

    const decodeVinData = async vin => {
        setIsLoading(true);

        try {
            const response = await decodeVin(vin);
            setVinData(response.data);
        } catch (error) {
            // we don't need to show an error message to user
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const vin = params.get('landingVin');

        if (vin) {
            decodeVinData(vin);
        }
    }, [params]);

    const signInChild = {
        label: `Sign In`,
        key: '0',
        children: (
            <SignInForm
                preview={params.get('preview')}
                setActiveTabKey={setActiveTabKey}
                openNotification={openNotification}
                t={t}
            />
        ),
    };

    const signUpChild = {
        label: `Sign Up`,
        key: '1',
        children: (
            <SignUpForm
                setActiveTabKey={setActiveTabKey}
                openNotification={openNotification}
                t={t}
                marketingConsent={marketingConsent}
                consentField={
                    <MarketingConsentCheckbox checked={marketingConsent} onChange={setMarketingConsent} t={t} />
                }
            />
        ),
    };

    const authType = {
        SIGN_IN: 'Sign-in',
        SIGN_UP: 'Sign-up',
    };

    if (isLoading) {
        return <AppLoader />;
    }

    return (
        <div className={classes['auth-wrapper']} style={{ gap: 0 }}>
            {contextHolder}

            {vinData?.brand && vinData?.brand !== '---' && (
                <Flex vertical align="center" justify="center" style={{ maxWidth: '80vw', marginTop: spacing[4] }}>
                    <Typography.Text style={{ color: colors.black, textAlign: 'center' }}>
                        {t('general:one-step-away')}
                    </Typography.Text>

                    <Typography.Text
                        style={{
                            fontSize: fontSize.lg,
                            fontWeight: '700',
                            marginTop: spacing[4],
                            color: primitiveColors.text,
                        }}
                    >
                        {`${vinData.brand} ${vinData.year !== '---' ? vinData.year : ''}`}
                    </Typography.Text>

                    <Tag
                        style={{
                            backgroundColor: primitiveColors.text,
                            color: colors.white,
                            paddingBlock: spacing[2],
                            paddingInline: spacing[3],
                            borderRadius: radius.md,
                            fontSize: fontSize.sm,
                            fontWeight: '700',
                            marginTop: spacing[4],
                        }}
                    >
                        {vinData.vin}
                    </Tag>
                </Flex>
            )}

            <div className={classes['auth-card']} style={{ marginTop: vinData?.brand ? '24px' : 0 }}>
                <a href={constants.LANDING_BASE_URL}>
                    <img src={logo} alt="CheckVIN.ai" className={classes.logo} />
                </a>
                <h3 style={{ marginBottom: '-12px', fontSize: isSmallDevice && 26 }}>
                    {activeTabKey === '0' ? t('auth:login-title') : t('auth:register-title')}
                </h3>
                <Tabs activeKey={activeTabKey} centered items={[signInChild, signUpChild]} />
                <div className={classes.content} style={{ gap: isSmallDevice && 0 }}>
                    <Flex justify="center" align="center" gap={spacing[0.5]}>
                        <p className={classes.subtitle}>
                            {activeTabKey === '0' ? t('auth:login-subtitle') : t('auth:register-subtitle')}
                        </p>
                        {/* the only way to the Sign-up tab (and its consent box): reachable by keyboard too */}
                        <a
                            className={classes['subtitle-link']}
                            role="button"
                            tabIndex={0}
                            onClick={toggleTab}
                            onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    toggleTab();
                                }
                            }}
                        >
                            {activeTabKey === '0' ? t('auth:login-subtitle-link') : t('auth:buttons.login')}
                        </a>
                    </Flex>

                    {activeTabKey === '0' && (
                        <Link to={routesPath.PUBLIC.PRIVACY_AND_TERMS} style={{ color: colors.black }}>
                            {t('auth:privacy-and-terms')}
                        </Link>
                    )}

                    <Flex vertical>
                        <Divider className={classes.divider} orientation="center" plain>
                            {t('auth:or')}
                        </Divider>

                        {/* <Row justify="center" align="center" style={{ marginBottom: spacing[3] }}> */}
                        <GoogleSignInButton
                            authType={activeTabKey === '1' ? authType.SIGN_UP : authType.SIGN_IN}
                            setActiveTabKey={setActiveTabKey}
                            openNotification={openNotification}
                            t={t}
                            landingQuery={getLandingVinQuery(params)}
                            marketingConsent={marketingConsent}
                        />

                        {/* </Row> */}

                        {/* <Row justify="center" align="center">
                            <FacebookSignInButton
                                authType={activeTabKey === '1' ? authType.SIGN_UP : authType.SIGN_IN}
                                setActiveTabKey={setActiveTabKey}
                                openNotification={openNotification}
                                t={t}
                            />
                        </Row> */}
                    </Flex>
                </div>
            </div>
        </div>
    );
}

export default Auth;
